/**
 * Analytics consent: which regional default applies, and what the visitor
 * chose. Pure logic + browser storage only — no framework imports, so it is
 * testable in isolation. The request-time plumbing that feeds
 * `classifyConsentMode()` lives in `consent-mode.ts`.
 *
 * Two modes, because one rule does not fit both audiences:
 *
 * - `opt-in`  — EEA/UK visitors. PostHog initializes with capturing OFF and
 *               stays off until Accept. Nothing is captured before the click,
 *               which is what ePrivacy requires for analytics cookies.
 * - `opt-out` — everyone else. PostHog captures from init, the notice says so,
 *               and Decline stops it. This is the CCPA posture the Privacy
 *               Policy describes, and it keeps anonymous marketing traffic
 *               measurable (most visitors never click anything).
 *
 * The visitor's choice is stored in localStorage, alongside the app's other
 * per-browser preferences (`theme`, dismissed announcements). It drives whether
 * the notice renders; posthog-js keeps its OWN persisted opt-in/out record,
 * which is what actually stops capture. `applyConsent()` in `posthog.ts` keeps
 * the two in agreement.
 */

/** Regional default for a visitor, decided server-side per request. */
export type ConsentMode = 'opt-in' | 'opt-out'

/** What the visitor chose, once they have chosen. */
export type ConsentChoice = 'granted' | 'denied'

/**
 * Bumped when the notice must be shown again — a new category of processing,
 * or a materially different Privacy Policy. A stored record at any other
 * version reads as "no choice yet", so the notice reappears.
 */
export const CONSENT_VERSION = 1

const STORAGE_KEY = 'masklabs.analytics-consent'

/**
 * EEA + UK, as ISO-3166-1 alpha-2. Includes the French outermost regions
 * (Cloudflare reports GF/GP/MQ/RE/YT separately from FR even though EU law
 * applies there) and the Crown Dependencies (UK GDPR). Switzerland is
 * deliberately absent: the revised FADP does not require prior consent for
 * analytics cookies.
 */
const EEA_COUNTRIES = new Set(
  // EU 27
  (
    'AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO ' +
    'SK SI ES SE ' +
    // French outermost regions
    'GF GP MQ RE YT ' +
    // EEA non-EU
    'IS LI NO ' +
    // UK + Crown Dependencies
    'GB GG JE IM'
  ).split(' '),
)

/**
 * Official languages of EEA/UK states that are not spoken as a majority
 * language anywhere else. Used only as a last resort — see
 * `classifyConsentMode()`. English, Spanish, Portuguese, German, French and
 * Dutch are absent on purpose: each has a large non-EEA population, so an
 * unqualified `es` or `en` says nothing about where the visitor is.
 */
const EEA_ONLY_LANGUAGES = new Set(
  'bg cs da el et fi ga hr hu is lt lv mt nb nn no pl ro sk sl sv'.split(' '),
)

/** Region subtags of an Accept-Language header, upper-cased, in order. */
function regionsFrom(acceptLanguage: string): string[] {
  return acceptLanguage
    .split(',')
    .map((part) => part.split(';')[0].trim())
    .flatMap((tag) => {
      const region = tag.split('-')[1]
      return region && /^[A-Za-z]{2}$/.test(region)
        ? [region.toUpperCase()]
        : []
    })
}

/** Primary language subtags of an Accept-Language header, lower-cased. */
function languagesFrom(acceptLanguage: string): string[] {
  return acceptLanguage
    .split(',')
    .map((part) => part.split(';')[0].trim().split('-')[0].toLowerCase())
    .filter((lang) => /^[a-z]{2,3}$/.test(lang))
}

/**
 * Which default a request gets. `country` is Cloudflare's `cf-ipcountry`
 * header, the authoritative signal; `acceptLanguage` is the fallback for when
 * it is missing (local dev, a request that bypassed Cloudflare, or IP
 * Geolocation switched off in the Cloudflare dashboard).
 *
 * The fallback matters: without it, a Cloudflare setting nobody remembers
 * turning off would silently put every visitor on `opt-in` and collapse the
 * analytics volume. With it, `en-US` still reads as `opt-out` and `en-GB`
 * still reads as `opt-in`.
 *
 * When nothing at all is known, the answer is `opt-in`. An unnecessary consent
 * prompt costs an event; capturing without consent where consent is required
 * costs more.
 */
export function classifyConsentMode(
  country: string | null | undefined,
  acceptLanguage?: string | null,
): ConsentMode {
  const code = country?.trim().toUpperCase()
  // 'XX' and 'T1' are Cloudflare's own values for "unknown" and "Tor exit",
  // not countries — fall through to the header rather than trusting them.
  if (code && /^[A-Z]{2}$/.test(code) && code !== 'XX' && code !== 'T1') {
    return EEA_COUNTRIES.has(code) ? 'opt-in' : 'opt-out'
  }

  if (acceptLanguage) {
    const regions = regionsFrom(acceptLanguage)
    if (regions.some((region) => EEA_COUNTRIES.has(region))) return 'opt-in'
    // A region subtag that is NOT in the EEA is a real signal (`en-US`), so
    // trust it rather than falling through to the fail-safe.
    if (regions.length > 0) return 'opt-out'
    if (languagesFrom(acceptLanguage).some((l) => EEA_ONLY_LANGUAGES.has(l))) {
      return 'opt-in'
    }
  }

  return 'opt-in'
}

/**
 * Reads a `?consent=opt-in|opt-out` override off a request URL, for looking at
 * both variants of the notice without a VPN or a browser language change.
 *
 * Pure and unguarded: the CALLER decides whether overrides are allowed, and
 * `consent-mode.ts` only honours this outside production. Returns null for a
 * missing or unrecognized value, so a stray `?consent=maybe` falls through to
 * the real classification rather than picking a mode at random.
 *
 * Safe against the page cache: the query string is part of Nitro's cache key,
 * so `/?consent=opt-in` occupies its own entry and cannot be served to someone
 * who asked for `/`.
 */
export function parseConsentOverride(url: string): ConsentMode | null {
  let value: string | null
  try {
    value = new URL(url).searchParams.get('consent')
  } catch {
    return null
  }
  return value === 'opt-in' || value === 'opt-out' ? value : null
}

type StoredConsent = { analytics: boolean; v: number }

/**
 * The visitor's stored choice, or `null` if they have not made one (or made it
 * under an older `CONSENT_VERSION`). Safe to call during SSR — returns `null`.
 */
export function readConsent(): ConsentChoice | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredConsent
    if (parsed?.v !== CONSENT_VERSION) return null
    return parsed.analytics ? 'granted' : 'denied'
  } catch {
    // Malformed JSON or storage blocked (private mode) — treat as no choice.
    return null
  }
}

/** Records the visitor's choice. Storage failures are non-fatal: the notice
 *  simply reappears on the next page load. */
export function writeConsent(choice: ConsentChoice): void {
  if (typeof window === 'undefined') return
  const value: StoredConsent = {
    analytics: choice === 'granted',
    v: CONSENT_VERSION,
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Ignored on purpose — see above.
  }
}

/**
 * Whether PostHog should start with capturing switched off: either the visitor
 * declined, or they are in an `opt-in` region and have not accepted yet.
 */
export function shouldStartOptedOut(
  mode: ConsentMode,
  choice: ConsentChoice | null,
): boolean {
  if (choice === 'granted') return false
  if (choice === 'denied') return true
  return mode === 'opt-in'
}
